import { activeLang, useT } from '../lib/i18n';
import { useEffect, useState, type FormEvent } from 'react';
import type { IntegrationDto } from '@bingo/shared';
import { api, ApiError } from '../lib/api';

const when = (iso: string | null, t: (s: string) => string) => (iso ? new Date(iso).toLocaleString(activeLang === 'fr' ? 'fr-FR' : 'en-GB', { dateStyle: 'short', timeStyle: 'short' }) : t('Never'));

/** Master only: one key per n8n flow, to send bookings to this app. */
export function Integrations() {
  const [list, setList] = useState<IntegrationDto[]>([]);
  const [path, setPath] = useState('');
  const [name, setName] = useState('');
  const [fresh, setFresh] = useState<{ name: string; key: string } | null>(null);
  const [copied, setCopied] = useState(false);
  const [confirmId, setConfirmId] = useState<string | null>(null);
  const [error, setError] = useState('');
  const t = useT();

  const load = () =>
    api<{ integrations: IntegrationDto[]; path: string }>('/api/owner/integrations')
      .then((r) => {
        setList(r.integrations);
        setPath(r.path);
      })
      .catch((e) => setError(e instanceof ApiError ? e.message : t('Could not load the keys.')));
  useEffect(() => {
    void load();
  }, []);

  async function create(e: FormEvent) {
    e.preventDefault();
    setError('');
    try {
      const r = await api<{ key: string }>('/api/owner/integrations', { method: 'POST', body: { name } });
      setFresh({ name, key: r.key });
      setCopied(false);
      setName('');
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('Could not create the key.'));
    }
  }

  async function revoke(id: string) {
    setError('');
    try {
      await api(`/api/owner/integrations/${id}`, { method: 'DELETE' });
      setConfirmId(null);
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('Could not revoke the key.'));
    }
  }

  const address = `${window.location.origin}${path}`;

  return (
    <div className="stack" style={{ maxWidth: 820 }}>
      <div className="page-head"><h1>{t('Integrations (n8n)')}</h1></div>
      <span className="muted">{t('One key per n8n flow. The flow sends each booking to this address with its key:')} <code>{address}</code>. {t('Bookings keep arriving while the app is locked.')}</span>
      {error && <div className="alert alert-bad">{error}</div>}

      {fresh && (
        <div className="card section">
          <h2>{t('Key for {name}', { name: fresh.name })}</h2>
          <div className="alert alert-warn">{t('Copy it now: it will never be shown again. Paste it only in n8n, never in a chat or an e-mail.')}</div>
          <code className="key-box">{fresh.key}</code>
          <div className="row-actions" style={{ justifyContent: 'flex-start' }}>
            <button className="btn btn-primary" onClick={() => navigator.clipboard.writeText(fresh.key).then(() => setCopied(true))}>
              {copied ? t('Copied') : t('Copy the key')}
            </button>
            <button className="btn" onClick={() => setFresh(null)}>{t('Done')}</button>
          </div>
        </div>
      )}

      <form className="card section" onSubmit={create}>
        <h2>{t('New key')}</h2>
        <div className="field">
          <label htmlFor="int-name">{t('Name of the flow')}</label>
          <input id="int-name" className="input" required minLength={2} maxLength={60} placeholder={t('e.g. BOKUN - BINGO')} value={name} onChange={(e) => setName(e.target.value)} />
        </div>
        <div><button className="btn btn-primary">{t('Create the key')}</button></div>
      </form>

      <div className="card">
        <table className="table">
          <thead><tr><th>{t('Flow')}</th><th>{t('Key')}</th><th>{t('Last call')}</th><th>{t('Last 7 days')}</th><th /></tr></thead>
          <tbody>
            {list.length === 0 && <tr><td colSpan={5} className="muted">{t('No key yet.')}</td></tr>}
            {list.map((i) => (
              <tr key={i.id} className={i.active ? '' : 'inactive'}>
                <td><b>{i.name}</b>{!i.active && <span className="badge badge-bad" style={{ marginLeft: 6 }}>{t('Revoked')}</span>}</td>
                <td><code>{i.keyHint}</code></td>
                <td className="muted">{when(i.lastUsedAt, t)}</td>
                <td>
                  {t('{a} new · {b} updated · {c} same · {d} skipped', { a: i.last7Days.created, b: i.last7Days.updated, c: i.last7Days.unchanged, d: i.last7Days.skipped })}
                  {i.last7Days.errors > 0 && <span className="badge badge-bad" style={{ marginLeft: 6 }}>{t('{n} errors', { n: i.last7Days.errors })}</span>}
                </td>
                <td>
                  <div className="row-actions">
                    {i.active && confirmId !== i.id && <button className="btn btn-sm btn-danger" onClick={() => setConfirmId(i.id)}>{t('Revoke')}</button>}
                    {confirmId === i.id && (
                      <>
                        <span className="muted" style={{ alignSelf: 'center', fontSize: 13 }}>{t('The flow will be refused at once.')}</span>
                        <button className="btn btn-sm btn-danger" onClick={() => revoke(i.id)}>{t('Yes, revoke')}</button>
                        <button className="btn btn-sm" onClick={() => setConfirmId(null)}>{t('Cancel')}</button>
                      </>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
